import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { isGuest } from '../utils/roleUtils';
import ConfirmationModal from '../components/ConfirmationModal';
import EventCard from '../components/EventCardNew';
import AttributeLabel from '../components/AttributeLabel';
import { StatCard, StatGroup } from '../components/StatCard';
import { ModalShell } from '../components/modals/core';
import Button from '../components/Button';
import {
  TicketIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';

interface GuestCard {
  id: number;
  name: string;
  price: number;
  total_entries: number;
  description?: string;
}

interface MyGuestCard {
  id: number;
  name: string;
  price: number;
  total_entries: number;
  remaining_entries: number;
  purchase_date: string;
  bezahlt_am?: string;
  bookings: EventBooking[];
}

interface EventBooking {
  kalender_id: number;
  termin_booking_id: number;
  booking_date: string;
  title: string;
  start: string;
  start_time: string;
  end_time?: string;
  creator_id: string;
  creator_name: string;
  creator_image?: string;
  creator_image_updated?: string;
  cotrainer_id?: string;
  cotrainer_name?: string;
  cotrainer_image?: string;
  cotrainer_image_updated?: string;
  max_teilnehmer?: number;
  participantCount: number;
  unique_id?: string;
  farbe?: string;
  cardName: string;
  cardId: number;
  gebucht_am?: string;
  gebucht_von_name?: string;
}

interface CancellationPeriods {
  members: string | number;
  guests: number;
}

const MyGuestCards: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const [guestCards, setGuestCards] = useState<GuestCard[]>([]);
  const [myGuestCards, setMyGuestCards] = useState<MyGuestCard[]>([]);
  const [selectedGuestCard, setSelectedGuestCard] = useState<GuestCard | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<{
    ticketId: number;
    kalenderId: number;
    title: string;
  } | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [cancellationPeriods, setCancellationPeriods] = useState<CancellationPeriods | null>(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
    onCancel: () => {}
  });
  
  const isGuestUser = isGuest(user);

  // Fetch cancellation periods
  const fetchCancellationPeriods = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/config/cancellation_periods');
      if (response.data.success) {
        setCancellationPeriods(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching cancellation periods:', error);
    }
  }, []);

  // Check if cancellation is allowed
  const canCancelEvent = useCallback((event: EventBooking, isTrainer: boolean = false): { allowed: boolean; reason?: string } => {
    if (!cancellationPeriods) return { allowed: false, reason: 'Stornierungsregeln werden geladen...' };
    
    const eventDate = new Date(`${event.start}T${event.start_time}`);
    const now = new Date();
    const hoursDiff = (eventDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    // Event is in the past
    if (hoursDiff < 0) {
      return { allowed: false, reason: 'Event ist bereits vorbei' };
    }
    
    // Check cancellation periods - guests use guest period
    const guestPeriod = cancellationPeriods.guests;
    
    if (typeof guestPeriod === 'number' && hoursDiff < guestPeriod) {
      return { allowed: false, reason: `Stornierung nur bis ${guestPeriod}h vor dem Event möglich` };
    }
    
    return { allowed: true };
  }, [cancellationPeriods]);

  const fetchGuestCards = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/guest-cards/types');
      const data = response.data;
      setGuestCards(data.cardTypes || []);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: err.response?.data?.message || 'Fehler beim Laden der Gästekarten',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          onCancel: () => {}
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyGuestCards = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/guest-cards/my-cards');
      const data = response.data;
      // Filter out NULL bookings and process the data
      const processedCards = data.cards.map((card: any) => ({
        ...card,
        bookings: (card.bookings || []).filter((booking: any) => booking !== null)
      }));
      setMyGuestCards(processedCards);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: err.response?.data?.message || 'Fehler beim Laden Ihrer Gästekarten',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          onCancel: () => {}
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isGuestUser) {
      fetchGuestCards();
      fetchMyGuestCards();
      fetchCancellationPeriods();
    }

    const interval = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [isGuestUser, fetchGuestCards, fetchMyGuestCards, fetchCancellationPeriods]);

  const handlePurchaseClick = (guestCard: GuestCard) => {
    setSelectedGuestCard(guestCard);
    setPurchaseModalOpen(true);
  };

  const handlePurchaseConfirm = async (guestCard: GuestCard) => {
    setPurchaseLoading(true);
    try {
      await apiClient.post('/api/guest-cards/purchase', {
        guest_card_type_id: guestCard.id
      });
      setPurchaseModalOpen(false);
      setCooldown(30);
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Gästekarte erfolgreich gekauft! Bitte zahlen Sie den Betrag in bar beim Trainer.',
        variant: 'single',
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          fetchMyGuestCards();
        },
        onCancel: () => {}
      });
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setPurchaseModalOpen(false);
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: err.response?.data?.message || 'Fehler beim Kauf der Gästekarte',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          onCancel: () => {}
        });
      }
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleCancelClick = (ticketId: number, kalenderId: number, title: string) => {
    // Find the event to check cancellation rules
    const event = myGuestCards
      .flatMap(card => card.bookings)
      .find(booking => booking.kalender_id === kalenderId);
      
    if (!event) return;
    
    const cancelCheck = canCancelEvent(event);
    
    if (!cancelCheck.allowed) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Stornierung nicht möglich',
        message: cancelCheck.reason || 'Stornierung nicht erlaubt.',
        variant: 'single',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
        onCancel: () => {}
      });
      return;
    }
    
    setSelectedBooking({ ticketId, kalenderId, title });
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Buchung stornieren',
      message: `Möchten Sie die Buchung "${title}" wirklich stornieren?\n\nDiese Aktion kann nicht rückgängig gemacht werden.`,
      variant: 'two',
      onConfirm: () => handleCancelConfirm({ ticketId, kalenderId, title }),
      onCancel: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
    });
  };

  const handleCancelConfirm = async (booking: { ticketId: number; kalenderId: number; title: string }) => {
    try {
      const { ticketId, kalenderId } = booking;
      const response = await apiClient.delete(`/api/members/cancel-event/${kalenderId}`);
      const data = response.data;
      
      if (data.success) {
        setMyGuestCards((prev) =>
          prev.map((card) =>
            card.id === ticketId
              ? {
                  ...card,
                  bookings: card.bookings.filter((entry) => entry.kalender_id !== kalenderId),
                  remaining_entries: card.remaining_entries + 1,
                }
              : card
          )
        );
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Buchung erfolgreich storniert.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          onCancel: () => {}
        });
      } else {
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: data.message || 'Fehler beim Stornieren der Buchung',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          onCancel: () => {}
        });
      }
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Stornieren',
        variant: 'single',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
        onCancel: () => {}
      });
    } finally {
      setSelectedBooking(null);
    }
  };

  const truncateText = (text: string, maxLength: number) =>
    text && text.length > maxLength ? text.substring(0, maxLength) + '...' : text || 'N/A';

  // Nur Vornamen der Trainer in den Event-Cards anzeigen
  const firstNameOnly = (name?: string) => (name ? name.trim().split(/\s+/)[0] : '');

  const cardProgress = (remaining: number, total: number) => Math.min((remaining / total) * 100, 100);

  const bookingHistory = myGuestCards
    .flatMap((card) =>
      (card.bookings || [])
        .filter((booking) => booking !== null && booking.start && booking.start_time && booking.title)
        .map((entry) => ({
          ...entry,
          // Use cardId and cardName from booking if available, fallback to card properties
          cardId: entry.cardId || card.id,
          cardName: entry.cardName || card.name,
        }))
    )
    .sort((a, b) => {
      const dateA = new Date(a.start);
      const dateB = new Date(b.start);
      return dateB.getTime() - dateA.getTime();
    });

  // Calculate statistics
  const totalCards = myGuestCards.length;
  const totalRemainingEntries = myGuestCards.reduce((sum, card) => sum + card.remaining_entries, 0);
  const totalUsedEntries = myGuestCards.reduce((sum, card) => sum + (card.total_entries - card.remaining_entries), 0);
  const unpaidCards = myGuestCards.filter(card => !card.bezahlt_am);
  const totalUnpaidAmount = unpaidCards.reduce((sum, card) => sum + card.price, 0);

  if (!isGuestUser) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-gray-50 dark:bg-gray-900 rounded-xl shadow-lg p-8 text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Zugriff verweigert
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Diese Funktion ist nur für Gäste verfügbar.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header - Variante 1: Einfacher Header */}
      <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <TicketIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Meine Gästekarten</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Verwalte deine Gästekarten und Event-Buchungen</p>
            </div>
          </div>
        </div>
      </header>
      
      {/* Statistics - nur wenn sinnvoll */}
      {myGuestCards.length > 0 && (
        <div className="mb-6">
          <StatGroup columns={4} mobileColumns={4}>
            <StatCard icon={TicketIcon} tone="primary" label="Meine Karten" value={totalCards} />
            <StatCard icon={CheckCircleIcon} tone="success" valueTone="success" label="Verfügbar" value={totalRemainingEntries} />
            <StatCard icon={CalendarDaysIcon} tone="info" valueTone="info" label="Genutzt" value={totalUsedEntries} />
            <StatCard icon={BanknotesIcon} tone="error" valueTone="error" label="Offen" value={`${totalUnpaidAmount}€`} />
          </StatGroup>
        </div>
      )}

      <div className="space-y-6">
        {/* Available Guest Cards */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-6">
            <TicketIcon className="h-6 w-6 text-primary-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Verfügbare Gästekarten
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Wähle eine Gästekarte für deine Events
              </p>
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : guestCards.length > 0 ? (
            <div className="grid gap-3">
              {guestCards.map((card) => (
                <div
                  key={card.id}
                  className="relative bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border-2 border-primary-500 overflow-hidden"
                >
                  {/* Status Badge */}
                  <div className="absolute top-0 right-0 px-3 py-1 rounded-bl-lg bg-primary-600 text-white text-xs font-semibold flex items-center gap-1">
                    <CheckCircleIcon className="h-3 w-3" />
                    <span>Verfügbar</span>
                  </div>
                  
                  <div className="pr-20">
                    {/* Card Title */}
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                      {card.name}
                    </h3>
                    
                    {/* Price and Training Info */}
                    <div className="flex items-center gap-4 mb-2">
                      <div className="flex items-center gap-2 text-sm">
                        <CurrencyEuroIcon className="h-4 w-4 text-success-600 dark:text-success-400" />
                        <span className="text-gray-700 dark:text-gray-300 font-medium">
                          {card.price} €
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CalendarDaysIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                        <span className="text-gray-700 dark:text-gray-300 font-medium">
                          {card.total_entries} Trainings
                        </span>
                      </div>
                    </div>
                    
                    {/* Additional Info */}
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <TicketIcon className="h-3 w-3 text-gray-400" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Gästekarte für Trainingseinheiten
                      </span>
                    </div>
                  </div>
                  
                  {/* Purchase Button */}
                  <button
                    onClick={() => handlePurchaseClick(card)}
                    disabled={cooldown > 0 || loading}
                    className={`absolute bottom-4 right-4 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                      cooldown > 0 || loading
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700 text-white'
                    }`}
                  >
                    {cooldown > 0 ? (
                      <>
                        <ClockIcon className="h-3 w-3" />
                        Warten ({cooldown}s)
                      </>
                    ) : (
                      <>
                        <BanknotesIcon className="h-3 w-3" />
                        Kaufen
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                <TicketIcon className="h-8 w-8 text-gray-400 dark:text-gray-600" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                Keine Gästekarten verfügbar
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                Bitte versuche es später erneut
              </p>
            </div>
          )}
        </section>

        {/* My Guest Cards */}
        {myGuestCards.length > 0 ? (
          <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-6">
              <TicketIcon className="h-6 w-6 text-primary-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Meine Gästekarten
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Deine gekauften Karten und dein Guthaben
                </p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {myGuestCards.map((card) => {
                const progress = cardProgress(card.remaining_entries, card.total_entries);
                const isPaid = !!card.bezahlt_am;
                const isEmpty = card.remaining_entries === 0;
                return (
                  <div
                    key={card.id}
                    className="flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5"
                  >
                    {/* Kopf: Name + Status-Pill */}
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/25 dark:text-primary-400">
                          <TicketIcon className="h-5 w-5" />
                        </span>
                        <h3 className="truncate text-base font-semibold text-gray-900 dark:text-white">
                          {card.name}
                        </h3>
                      </div>
                      <span
                        className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                          isPaid
                            ? 'bg-success-50 text-success-700 dark:bg-success-900/25 dark:text-success-400'
                            : 'bg-error-50 text-error-700 dark:bg-error-900/25 dark:text-error-400'
                        }`}
                      >
                        {isPaid ? (
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                        ) : (
                          <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                        )}
                        {isPaid ? 'Bezahlt' : 'Offen'}
                      </span>
                    </div>

                    {/* Zahlungshinweis */}
                    {!isPaid && (
                      <div className="mb-4 flex items-center gap-2 rounded-lg border border-error-200 bg-error-50 px-3 py-2 dark:border-error-800 dark:bg-error-900/20">
                        <BanknotesIcon className="h-4 w-4 flex-shrink-0 text-error-600 dark:text-error-400" />
                        <p className="text-sm text-error-700 dark:text-error-300">
                          Bitte zahle den Betrag{' '}
                          <strong className="font-semibold">in bar beim Trainer</strong>.
                        </p>
                      </div>
                    )}

                    {/* Guthaben / Fortschritt */}
                    <div className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-700/60 dark:bg-gray-900/40">
                      <div className="mb-2 flex items-baseline justify-between">
                        <AttributeLabel
                          iconKey="count"
                          size="sm"
                          textClassName="text-xs text-gray-500 dark:text-gray-400"
                        >
                          Verbleibende Trainings
                        </AttributeLabel>
                        <span className="text-sm font-semibold">
                          <span
                            className={
                              isEmpty
                                ? 'text-error-600 dark:text-error-400'
                                : 'text-primary-600 dark:text-primary-400'
                            }
                          >
                            {card.remaining_entries}
                          </span>
                          <span className="text-gray-400 dark:text-gray-500"> / {card.total_entries}</span>
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${
                            isEmpty ? 'bg-error-500' : 'bg-primary-600'
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    {/* Meta: Betrag, Kaufdatum, Bezahlt am */}
                    <div className="mt-auto grid grid-cols-3 gap-x-4 gap-y-3 border-t border-gray-100 pt-4 dark:border-gray-700/60">
                      <div>
                        <AttributeLabel
                          iconKey="amount"
                          size="sm"
                          textClassName="text-xs text-gray-500 dark:text-gray-400"
                        >
                          Betrag
                        </AttributeLabel>
                        <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">
                          {card.price} €
                        </p>
                      </div>
                      {card.purchase_date && (
                        <div>
                          <AttributeLabel
                            iconKey="date"
                            size="sm"
                            textClassName="text-xs text-gray-500 dark:text-gray-400"
                          >
                            Kaufdatum
                          </AttributeLabel>
                          <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">
                            {new Date(card.purchase_date).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      )}
                      {isPaid && card.bezahlt_am && (
                        <div>
                          <AttributeLabel
                            iconKey="payment"
                            size="sm"
                            textClassName="text-xs text-gray-500 dark:text-gray-400"
                          >
                            Bezahlt am
                          </AttributeLabel>
                          <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">
                            {new Date(card.bezahlt_am).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : (
          <section className="bg-primary-50 dark:bg-primary-900/20 rounded-xl shadow-sm p-8 text-center border border-primary-200 dark:border-primary-700">
            <TicketIcon className="h-12 w-12 text-primary-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Kaufe eine Gästekarte, um Trainings zu buchen!
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 max-w-md mx-auto">
              Du zahlst den Betrag in bar beim Trainer. Deine Gästekarte ist sofort nach dem Kauf für Buchungen nutzbar.
            </p>
          </section>
        )}

        {/* Booking History */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-6">
            <ClockIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Ihre Buchungen
            </h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : bookingHistory.length > 0 ? (
            <div className="grid gap-3">
              {bookingHistory.map((entry) => {
                // Transform booking data to EventCard format
                const eventCardData = {
                  id: entry.kalender_id,
                  title: entry.title,
                  start: entry.start,
                  start_time: entry.start_time,
                  end_time: entry.end_time,
                  creator_name: firstNameOnly(entry.creator_name),
                  creator_image: entry.creator_image,
                  creator: entry.creator_id,
                  cotrainer: entry.cotrainer_id,
                  cotrainer_name: firstNameOnly(entry.cotrainer_name),
                  cotrainer_image: entry.cotrainer_image,
                  max_teilnehmer: entry.max_teilnehmer,
                  participantCount: entry.participantCount,
                  unique_id: entry.unique_id,
                  farbe: entry.farbe,
                  cardName: entry.cardName,
                  booking_date: entry.booking_date,
                  cardId: entry.cardId,
                  kalender_id: entry.kalender_id
                };
                
                // Check cancellation status for this event
                const cancelStatus = canCancelEvent(entry);
                
                return (
                  <EventCard
                    key={`${entry.cardId}-${entry.kalender_id}-${entry.termin_booking_id}`}
                    event={eventCardData}
                    showCancelButton={true}
                    onCancel={() => handleCancelClick(entry.cardId, entry.kalender_id, entry.title)}
                    BASE_URL={BASE_URL}
                    isGuestCardStyle={false}
                    cancellationStatus={cancelStatus}
                  />
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              Keine Buchungen vorhanden.
            </p>
          )}
        </section>
      </div>


      {/* Kauf-Bestätigung – ModalShell (confirm), mobil als Slide-up */}
      <ModalShell
        open={purchaseModalOpen}
        onClose={() => !purchaseLoading && setPurchaseModalOpen(false)}
        variant="confirm"
        size="sm"
        accent="brand"
        icon={TicketIcon}
        title="Gästekarte kaufen"
        mobilePresentation="sheet"
        closeOnBackdrop={!purchaseLoading}
        closeOnEscape={!purchaseLoading}
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <Button
              variant="secondary"
              onClick={() => setPurchaseModalOpen(false)}
              disabled={purchaseLoading}
              className="min-h-[44px] w-full justify-center sm:w-auto"
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={() => selectedGuestCard && handlePurchaseConfirm(selectedGuestCard)}
              loading={purchaseLoading}
              icon={<BanknotesIcon className="h-5 w-5" />}
              className="min-h-[44px] w-full justify-center sm:w-auto"
            >
              {purchaseLoading
                ? 'Wird gekauft…'
                : selectedGuestCard
                  ? `Für ${selectedGuestCard.price} € kaufen`
                  : 'Kaufen'}
            </Button>
          </div>
        }
      >
        {selectedGuestCard && (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              Möchtest du die Gästekarte{' '}
              <strong className="font-semibold text-gray-900 dark:text-white">
                {selectedGuestCard.name}
              </strong>{' '}
              mit{' '}
              <strong className="font-semibold text-gray-900 dark:text-white">
                {selectedGuestCard.total_entries} Trainings
              </strong>{' '}
              für{' '}
              <strong className="font-semibold text-gray-900 dark:text-white">
                {selectedGuestCard.price} €
              </strong>{' '}
              kaufen?
            </p>
            <div className="flex items-start gap-2.5 rounded-lg border border-info-300 bg-info-50 p-3 dark:border-info-700 dark:bg-info-900/20">
              <BanknotesIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-info-600 dark:text-info-400" />
              <p className="text-sm text-info-700 dark:text-info-300">
                Die Bezahlung erfolgt{' '}
                <strong className="font-semibold">in bar beim Trainer</strong>. Deine Gästekarte
                ist sofort nach dem Kauf nutzbar.
              </p>
            </div>
          </div>
        )}
      </ModalShell>

      {/* Confirmation Modal für Feedback */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        destructive={confirmModal.variant === 'two' && confirmModal.type === 'warning' && /stornier/i.test(confirmModal.title || '')}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: confirmModal.onCancel
          },
          confirm: {
            text: 'Bestätigen',
            onPress: confirmModal.onConfirm
          }
        } : undefined}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: confirmModal.onConfirm
        } : undefined}
      />
    </div>
  );
};

export default MyGuestCards;
